import type { IGetListDynamic, IRatingMatrixFramework } from '__generated__';
import { baseApi, frameworkListTag } from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  getAllDynamic,
  normalizeError,
  type IDynamicResult,
  type PatchBody,
} from 'utils/api-client';
import { buildFrameworkSearchFilter } from './helpers';
import type {
  CreateFrameworkPayload,
  GetFrameworkListArgs,
  IRatingMatrixFrameworkListItem,
} from './models';

export const ratingMatrixFrameworkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFrameworkList: builder.query<IRatingMatrixFrameworkListItem[], GetFrameworkListArgs>({
      async queryFn(args) {
        try {
          const { value } = await getAllDynamic<IRatingMatrixFramework>(
            (body) =>
              APIRead.api.ratingMatrixFrameworkGetListDynamicCreate(
                body as Partial<IGetListDynamic>,
              ) as Promise<{
                data: IRatingMatrixFramework[] | IDynamicResult<IRatingMatrixFramework>;
              }>,
            {
              includeArchived: args.includeArchived,
              filter: buildFrameworkSearchFilter(args.search),
              count: false,
            } as Partial<IGetListDynamic>,
          );

          return { data: value as IRatingMatrixFrameworkListItem[] };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: [frameworkListTag],
    }),

    createFramework: builder.mutation<IRatingMatrixFramework, CreateFrameworkPayload>({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: [frameworkListTag],
    }),

    cloneFramework: builder.mutation<number, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkCloneCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: [frameworkListTag],
    }),

    archiveFramework: builder.mutation<string, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkArchiveCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: [frameworkListTag],
    }),

    getFullFramework: builder.query<IRatingMatrixFramework, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await APIRead.api.ratingMatrixFrameworkGetFullFrameworkDetail({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [{ type: 'RatingMatrixFramework', id: arg.id }],
    }),

    patchFramework: builder.mutation<
      string,
      { id: number; patch: PatchBody<IRatingMatrixFramework> }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        frameworkListTag,
        { type: 'RatingMatrixFramework', id: arg.id },
      ],
    }),
  }),
});

export const {
  useGetFrameworkListQuery,
  useLazyGetFrameworkListQuery,
  useCreateFrameworkMutation,
  useCloneFrameworkMutation,
  useArchiveFrameworkMutation,
  useGetFullFrameworkQuery,
  useLazyGetFullFrameworkQuery,
  usePatchFrameworkMutation,
} = ratingMatrixFrameworkApi;
